import { logger } from './logger';

export const isDefined = <T>(x: T | undefined): x is T => x !== undefined;

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export function isLiteralType<T extends string>(str: string, allowedValues: readonly T[]): str is T {
    return allowedValues.includes(str as T);
}

export function toLiteralType<T extends string>(str: string, name: string, allowedValues: readonly T[]): T {
    if (isLiteralType<T>(str, allowedValues)) {
        return str;
    }
    const error = `Not a valid ${name}: ${str}`;
    logger.error(error);
    throw new Error(error);
}

/**
 * Utility types for type-safe dot notation strings
 * See https://stackoverflow.com/a/68412497
 */
type PathImpl<T, Key extends keyof T> = Key extends string
    ? T[Key] extends Record<string, any>
        ? `${Key}.${PathImpl<T[Key], keyof T[Key]>}` | `${Key}.${Exclude<keyof T[Key], keyof any[]> & string}`
        : never
    : never;

export type DotNotationStringPath<T> = PathImpl<T, keyof T> | keyof T;

export type DotNotationStringValue<T, P extends DotNotationStringPath<T>> = P extends `${infer Key}.${infer Rest}`
    ? Key extends keyof T
        ? Rest extends DotNotationStringPath<T[Key]>
            ? DotNotationStringValue<T[Key], Rest>
            : never
        : never
    : P extends keyof T
      ? T[P]
      : never;
